import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Briefcase, Plus } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { StackedList, Pagination } from "@/components/shared/StackedList";
import { LoadingState, ErrorState, EmptyState } from "@/components/shared/States";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";

export default function Clients() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [q, setQRaw] = useState("");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;
  const setQ = (v) => { setQRaw(v); setPage(1); }; // a new search always starts at page 1
  const [source, setSourceRaw] = useState("platform");
  const setSource = (v) => { setSourceRaw(v); setPage(1); };
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", tier: "", client_since: "" });
  const navigate = useNavigate();

  const load = () => {
    const params = { page, page_size: PAGE_SIZE, source, ...(q ? { q } : {}) };
    api.get("/directory/clients", { params }).then((r) => setData(r.data)).catch(() => setError(true));
  };
  useEffect(() => { load(); }, [q, page, source]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    try {
      await api.post("/directory/clients", form);
      toast.success("Client organisation created");
      setOpen(false);
      setForm({ name: "", tier: "", client_since: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create client"); }
  };

  if (error) return <ErrorState testId="clients-error" />;

  return (
    <div data-testid="clients-page" className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-[24px] font-semibold leading-tight tracking-[-0.01em] text-jigsaw-navy">Clients</h1>
          <p className="text-sm text-jigsaw-muted">Client organisations Jigsaw sources accommodation for.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-client-button" className="bg-jigsaw-orange text-white hover:bg-jigsaw-orangeDark"><Plus className="mr-1 h-4 w-4" /> New Client</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New client organisation</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Name</Label><Input data-testid="client-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>Tier</Label><Input value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })} placeholder="e.g. STRATEGIC" /></div>
              <div><Label>Client since</Label><Input value={form.client_since} onChange={(e) => setForm({ ...form, client_since: e.target.value })} placeholder="e.g. 2026" /></div>
            </div>
            <DialogFooter><Button data-testid="client-save-button" onClick={submit} disabled={!form.name}>Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Input data-testid="clients-search-input" placeholder={source === "legacy" ? "Search legacy clients by company or contact..." : "Search clients..."} value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs" />
        <div data-testid="clients-source-toggle" className="inline-flex rounded-full border border-jigsaw-border p-0.5 text-sm">
          {[["platform", "Client organisations"], ["legacy", "Legacy records (read-only)"]].map(([k, label]) => (
            <button key={k} data-testid={`clients-source-${k}`} onClick={() => setSource(k)} className={`rounded-full px-3 py-1 ${source === k ? "bg-jigsaw-navy text-white" : "text-jigsaw-muted hover:text-jigsaw-navy"}`}>{label}</button>))}
        </div>
      </div>
      {source === "legacy" && <p data-testid="clients-legacy-note" className="text-xs text-jigsaw-muted">Business and contact records imported from the previous portal. Historical records have no portal access; open a record to see its booking history.</p>}

      {!data ? (
        <LoadingState testId="clients-loading" />
      ) : data.items.length === 0 ? (
        <EmptyState testId="clients-empty" icon={Briefcase} title="No clients found" />
      ) : (
        <div>
          <StackedList testId="clients-stacked" items={data.items} title={(c) => c.name} onOpen={(c) => navigate(c.source === "legacy" ? `/control-centre/clients/legacy/${c.id}` : `/control-centre/clients/${c.id}`)} openLabel="Open client"
            fields={source === "legacy" ? [{ label: "Contact", value: (c) => c.contact_name }, { label: "City", value: (c) => c.city }] : [{ label: "Tier", value: (c) => c.tier }, { label: "Client since", value: (c) => c.client_since }]} />
          <div className="hidden sm:block">
          <Table data-testid="clients-table">
            <TableHeader><TableRow><TableHead>Name</TableHead>{source === "legacy" ? <><TableHead>Contact</TableHead><TableHead>City</TableHead></> : <><TableHead>Tier</TableHead><TableHead>Client since</TableHead></>}</TableRow></TableHeader>
            <TableBody>
              {data.items.map((c) => (
                <TableRow key={c.id} data-testid={`client-row-${c.id}`} className="cursor-pointer hover:bg-jigsaw-bg" onClick={() => navigate(c.source === "legacy" ? `/control-centre/clients/legacy/${c.id}` : `/control-centre/clients/${c.id}`)}>
                  <TableCell className="font-medium text-jigsaw-navy">{c.name}{c.source === "legacy" && <span className="ml-2 rounded-full border border-jigsaw-border px-2 py-0.5 text-xs text-jigsaw-muted">legacy</span>}</TableCell>
                  {source === "legacy" ? <><TableCell>{c.contact_name || "—"}</TableCell><TableCell>{c.city || "—"}</TableCell></> : <><TableCell>{c.tier || "—"}</TableCell><TableCell>{c.client_since || "—"}</TableCell></>}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
          <Pagination testId="clients-pagination" page={data.page} pageSize={data.page_size} total={data.total} onPage={setPage} />
        </div>
      )}
    </div>
  );
}
